/**
 * Masking secrets inside a target text or a driver's error (Option C C2 review C2-03, its r2 / r3 re-checks, and the
 * security slice 2026-09-28). Split out of `targetSecrets.ts`, which re-exports it; `Docs/ConnectionSecrets.md` has the
 * contract. Quote-aware (`"…"` / `'…'` with doubled quotes and backslash escapes, ODBC `{…}` with `}}`); a value it cannot
 * delimit is masked to the end of the text (fails closed); idempotent.
 */

import {
  applySpans, decodedView, indexText, isSpace, slashCredentialSpans, urlUserinfoSpans, withoutUrlControls,
  type Span, type TextIndex,
} from './secretTextScanners';
import { jsonMemberSpans } from './secretJsonScanner';
import { keyWords, namesSomethingElse } from './secretKeyNames';

/**
 * Keys whose value is a secret wherever a target text carries one — matched case-insensitively, after any
 * non-alphanumeric character (so `DB_PASSWORD`, `MY_WALLET_PASSWORD` and `User Password` count). Sources, as installed
 * on 2026-09-28 (review r3):
 * - SQL Server: @tediousjs/connection-string 0.6.0 `lib/parser/sql-connection-string.js` — `Password`, alias `PWD`.
 * - PostgreSQL: pg-connection-string 2.9.1 `index.js` — URL userinfo and the `password` query parameter; libpq's
 *   `sslpassword`.
 * - MySQL: mysql2 3.16.3 `lib/connection_config.js` — `password`, `password1`–`password3`, `passwordSha1`, URL userinfo.
 * - Oracle: oracledb 6.10.0 `lib/oracledb.js` — `password`, `newPassword`, `walletPassword`; descriptors in the wild
 *   carry `MY_WALLET_PASSWORD`; JDBC thin / SQL*Plus `user/password@…` (below).
 * - Kusto: azure-kusto-data 7.2.0 `dist-esm/src/connectionBuilderBase.js` (`isSecret`) — `Password` / `Pwd`,
 *   `Application Key` / `AppKey`, `Application Certificate PrivateKey`; its builder's `accessToken`; Kusto .NET's
 *   `Application Token` / `AppToken`, `User Token` / `UserToken` / `UsrToken`.
 * - Azure Storage: @azure/storage-blob 12.33.0 `dist/commonjs/utils/utils.common.js` — `AccountKey`,
 *   `SharedAccessSignature`; a SAS's `sig`; Service Bus / Event Hubs `SharedAccessKey`.
 * - Entra / OAuth: `client_secret` / `ClientSecret`, `access_token`, `refresh_token`, `id_token`; generic `secret`,
 *   `token`, `api_key`, `private_key`.
 * - HTTP (RFC 9110): an `Authorization` header / field (Azure SDK errors carry their request's headers).
 * - Environment variables the CLIs read (Fable review S8): libpq `PGPASSWORD`, sqlcmd `SQLCMDPASSWORD` (`MYSQL_PWD`
 *   already matches `pwd` after `_`).
 * - SSH: a private key's `passphrase` (the app's own `ssh.keyPassphrase` slot; review round 5).
 */
const SECRET_KEYS = [
  'my_wallet_password', 'wallet\\s*password', 'new\\s*password', 'ssl\\s*password', 'password(?:sha1|[123])?',
  'passwd', 'pwd', 'client_?secret', 'access_?token', 'refresh_?token', 'id_?token', 'app(?:lication)?\\s*token',
  'user\\s*token', 'usr\\s*token', 'app(?:lication)?\\s*key', 'application\\s*certificate\\s*private\\s*key',
  'private[_-]?key', 'api[_-]?key', 'sharedaccesssignature', 'sharedaccesskey', 'accountkey', 'authorization', 'secret',
  'token', 'sig', 'pgpassword', 'sqlcmdpassword', 'passphrase',
].join('|');
/** `key=value`, and the `key: value` form (util.inspect, YAML-ish text; Fable review S8). */
const SECRET_KEY = new RegExp(`(?<![A-Za-z0-9])(?:${SECRET_KEYS})\\s*[=:]\\s*`, 'giu');
const SECRET_KEY_NAME = new RegExp(`(?<![A-Za-z0-9])(?:${SECRET_KEYS})$`, 'iu');

/**
 * Longest text the redactor reads (security-slice review SBT-06). Longer text — and any text the scan fails on — is
 * withheld whole as `WITHHELD_TEXT` rather than half-checked.
 */
export const MAX_REDACTION_INPUT = 262_144;
export const WITHHELD_TEXT = '[withheld: too long to check for secrets]';

/** True when a key (a descriptor node, a connection-string keyword, a URL / JSON key) names a secret; `%XX` decoded. */
export function isSecretKeyName(key: string): boolean {
  if (key.length > 256) return false;
  const name = decodedView(key.trim()).view;
  const words = keyWords(name);
  // Classified by the WHOLE key (review round 6, Codex R6-3): a name that IS the secret (`password`, `sshPassphrase`,
  // `apiKey`, `sasToken`) — never a flag about it or a non-credential token (`secretKeyNames.ts`).
  if (namesSomethingElse(words)) return false;
  // A camelCase boundary counts as a word boundary for a KEY (`sshPassphrase`, `dbPassword`; round 5, Fable N8) — only
  // for key names, never inside free text.
  return SECRET_KEY_NAME.test(name) || SECRET_KEY_NAME.test(words.join('_'));
}

type SecretContext = 'descriptor' | 'url' | 'pairs' | 'colon';

/**
 * What ends a bare value where the key sits: `)` in a descriptor, `&` / `#` in a URL query — never whitespace: the URL
 * parser drops tabs and newlines and pg-connection-string encodes spaces, so both stay inside the value (SBT-01) — and
 * `;` in key=value pairs, never a line break (SQL Server's parser keeps it; review r3). `;;` is a literal `;` to SQL
 * Server's parser.
 */
const TERMINATORS: Record<SecretContext, string> = { descriptor: ')', url: '&#', pairs: ';', colon: ',;}]\n\r' };

/**
 * The context of the key at `keyStart`, from the first character before its name (and a `DB_` style prefix) and
 * spaces: `(` a descriptor; `?` a URL query, and `&` too when the same whitespace-free token has a `?` (a SAS token or
 * `App=a&Password=…` without one is key=value pairs — `;`-terminated, the safer reading); anything else pairs.
 */
function contextOf(text: string, index: TextIndex, keyStart: number): SecretContext {
  let i = keyStart - 1;
  while (i >= 0 && /\w/u.test(text[i])) i -= 1;
  while (i >= 0 && isSpace(text[i])) i -= 1;
  if (text[i] === '(') return 'descriptor';
  if (text[i] === '?') return 'url';
  if (text[i] !== '&') return 'pairs';
  return index.prevQuery[i] > index.prevSpace[i] ? 'url' : 'pairs';
}

/** Index after the quoted (`"…"`, `'…'`) or braced (`{…}`) value starting at `start`, or -1 when it never closes. */
function closeOf(text: string, start: number): number {
  const close = text[start] === '{' ? '}' : text[start];
  for (let i = start + 1; i < text.length; i += 1) {
    if (close !== '}' && text[i] === '\\') i += 1;
    else if (text[i] === close && text[i + 1] === close) i += 1;
    else if (text[i] === close) return i + 1;
  }
  return -1;
}

/**
 * Where the secret value starting at `start` ends; the end of the text when it cannot be delimited safely: an
 * unterminated quote / brace, a quote or brace mid-value, a `(` in a descriptor value, or a backslash before a quote or
 * another backslash — the shape of serialized JSON, where the real escapes are one level down (SBT-03). A backslash
 * before anything else escapes it (Oracle's `prefix\)rest`).
 */
function secretValueEnd(text: string, start: number, context: SecretContext): number {
  let i = start;
  if ('"\'{'.includes(text[i] ?? ' ')) {
    i = closeOf(text, i);
    if (i < 0) return text.length;
    // `key: 'value'` (an object literal / inspect output): the value is the quoted string.
    if (context === 'colon') return i;
  }
  for (; i < text.length; i += 1) {
    const c = text[i];
    if (c === '\\') {
      if ('\\"\''.includes(text[i + 1] ?? '')) return text.length;
      i += 1;
    } else if (context === 'pairs' && c === ';' && text[i + 1] === ';') {
      i += 1;
    } else if (TERMINATORS[context].includes(c)) {
      return i;
    } else if ('"\'{'.includes(c) || (context === 'descriptor' && c === '(')) {
      return text.length;
    }
  }
  return text.length;
}

function keyValueSpans(text: string, index: TextIndex, spans: Span[], urlOnly = false): void {
  const { view, origin } = decodedView(text);
  SECRET_KEY.lastIndex = 0;
  let floor = 0;
  for (let match = SECRET_KEY.exec(view); match; match = SECRET_KEY.exec(view)) {
    const keyStart = origin[match.index];
    const valueStart = origin[match.index + match[0].length];
    if (keyStart < floor) continue;
    const colon = match[0].trimEnd().endsWith(':');
    const context = colon ? 'colon' : contextOf(text, index, keyStart);
    if (urlOnly && context !== 'url') continue;
    const end = secretValueEnd(text, valueStart, context);
    spans.push([valueStart, end]);
    floor = Math.max(end, valueStart);
    if (end >= text.length) return;
  }
}

/**
 * A target text (a server field, a connect string, a driver's error) with its embedded secrets masked: a URL's
 * password, an Oracle `user/password@`, a JSON member named like a secret, and the value of every secret-named key —
 * `Password=`, `PWD={…}`, `AccountKey=`, `AppKey=`, a SAS's `sig=` (also `%73ig=`), `MY_WALLET_PASSWORD="…"`. What a
 * user typed into Server is shown, never a secret inside it. Idempotent; a value it cannot delimit is masked to the
 * end of the text; text over `MAX_REDACTION_INPUT`, or text the scan fails on, is `WITHHELD_TEXT`. Linear time.
 */
export function redactTargetSecrets(text: string): string {
  if (text.length > MAX_REDACTION_INPUT) return WITHHELD_TEXT;
  try {
    return redactAt(text, 0);
  } catch {
    return WITHHELD_TEXT;
  }
}

/** JSON serialized inside JSON inside JSON… deeper than this is masked whole (fails closed; Fable review S4). */
const MAX_NESTED_JSON = 6;

function redactAt(text: string, depth: number): string {
  const spans = secretSpans(text, depth);
  // The WHATWG URL parser — pg-connection-string's and mysql2's — removes every TAB, LF and CR before it parses, so
  // `?pass<TAB>word=` names the password to the driver (review round 2, Codex #1). The text is scanned a second time
  // as that parser reads it — URL userinfo and query keys only; a line break still ends a `key: value` line — and those
  // spans are mapped back to the original positions; both sets are masked.
  if (/[\t\n\r]/u.test(text)) {
    const { view, origin } = withoutUrlControls(text);
    for (const [start, end] of urlSpans(view)) spans.push([origin[start], origin[end]]);
  }
  return applySpans(text, spans);
}

/** What only the URL parser reads: userinfo passwords and query values named like a secret. */
function urlSpans(text: string): Span[] {
  const index = indexText(text);
  const spans: Span[] = [];
  urlUserinfoSpans(text, index, spans);
  keyValueSpans(text, index, spans, true);
  return spans;
}

function secretSpans(text: string, depth: number): Span[] {
  const index = indexText(text);
  const spans: Span[] = [];
  urlUserinfoSpans(text, index, spans);
  slashCredentialSpans(text, index, spans);
  // A JSON string holding escaped JSON (`"{\"password\": …}"`) is decoded and checked one level down; when that
  // level had anything to mask, the whole string is masked.
  const nestedHasSecret = (decoded: string) => depth >= MAX_NESTED_JSON || redactAt(decoded, depth + 1) !== decoded;
  jsonMemberSpans(text, isSecretKeyName, spans, nestedHasSecret);
  keyValueSpans(text, index, spans);
  return spans;
}
